import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Info, RotateCcw, Save, Palette, Cpu } from 'lucide-react';
import { useUniformLoading, loadingConfigs } from '@/hooks/useUniformLoading';
import { companyService, type Company } from '@/services/companyService';
import { DEFAULT_COMPANY_ID } from '@/utils/branding';
import { getImageURL } from '@/utils/ERPUtils';
import { phoneRules } from '@/utils/phoneValidation';
import { useUserRoles } from '@/hooks/useUserRoles';

import KBSettingsPanel from '@/components/admin/company/KBSettingsPanel';
import { MobileLogoCropUpload } from './MobileLogoCropUpload';

import { logger } from '@/utils/logger';
interface CompanyBrandingFormData {
  organization_name: string;
  domain: string;
  contact_number: string;
  logo_url: string;
}

export const MobileCompanyBranding: React.FC = () => {
  const [companyData, setCompanyData] = useState<Company | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const TARGET_WIDTH = 220;
  const TARGET_HEIGHT = 90;

  const { roles, isSuperAdmin, loading: rolesLoading } = useUserRoles();

  const isAdmin = isSuperAdmin || roles.some(role => role.name === 'Admin');

  // Mirrors CompanyBrandingPage (desktop): the Brain Settings backend
  // allows System Manager + Admin; the Agent Base routing row inside the
  // panel stays System-Manager-only, so we pass `isSystemManager` down.
  const isSystemManager = isSuperAdmin || roles.some(role => role.name === 'System Manager');
  const canManageKB = isSystemManager || isAdmin;

  // Two-tab surface matching desktop: Branding + KB Settings. The header's
  // Reset/Save buttons drive the Branding form only, so they hide on the
  // KB Settings tab (which owns its own save lifecycle).
  const [activeTab, setActiveTab] = useState<'branding' | 'brain-settings'>('branding');

  const { shouldShowLoader } = useUniformLoading({
    loading: isLoading || rolesLoading,
    ...loadingConfigs.form
  });

  const form = useForm<CompanyBrandingFormData>({
    defaultValues: {
      organization_name: '',
      domain: '',
      contact_number: '',
      logo_url: '',
    }
  });

  const { reset, formState: { isDirty } } = form;

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setIsLoading(true);
        setCompanyId(DEFAULT_COMPANY_ID);

        const data = await companyService.getCompanyDetails(DEFAULT_COMPANY_ID);
        setCompanyData(data);

        reset({
          organization_name: data.organization_name || '',
          domain: data.domain || '',
          contact_number: data.contact_number || '',
          logo_url: data.company_logo || '',
        });
      } catch (error) {
        logger.error('Failed to fetch company data:', error);
        toast.error('Failed to load company information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyData();
  }, [reset]);

  const handleSave = async (data: CompanyBrandingFormData) => {
    if (!companyId) {
      toast.error('Company ID not found');
      return;
    }

    try {
      setIsSaving(true);

      let logoUrl = data.logo_url;
      if (logoFile) {
        toast.info('Uploading logo...');
        logoUrl = await companyService.uploadCompanyLogo(companyId, logoFile);
      }

      // Send the raw string (not `|| undefined`): an empty string clears the
      // field server-side, whereas `undefined` is dropped from the JSON body,
      // so a cleared Website / Contact Number would never actually save.
      const updateData: Partial<Company> = {
        organization_name: data.organization_name,
        domain: data.domain ?? '',
        contact_number: data.contact_number ?? '',
        company_logo: logoUrl || '',
      };

      const updatedCompany = await companyService.updateCompanyDetails(companyId, updateData);
      setCompanyData(updatedCompany);

      reset({
        organization_name: updatedCompany.organization_name || '',
        domain: updatedCompany.domain || '',
        contact_number: updatedCompany.contact_number || '',
        logo_url: updatedCompany.company_logo || '',
      });

      setLogoFile(null);
      toast.success('Company branding updated successfully');
    } catch (error) {
      logger.error('Failed to save company branding:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (companyData) {
      reset({
        organization_name: companyData.organization_name || '',
        domain: companyData.domain || '',
        contact_number: companyData.contact_number || '',
        logo_url: companyData.company_logo || '',
      });
      setLogoFile(null);
      toast.info('Changes discarded');
    }
  };

  if (shouldShowLoader) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as 'branding' | 'brain-settings')}
      className="h-full flex flex-col bg-background"
    >
      {/* Header Section */}
      <div className="flex-shrink-0 px-4 pt-6 pb-4">
        {/* Title */}
        <h1 className="text-2xl font-semibold text-foreground text-center mb-4">
          My Company
        </h1>

        {/* Branding / KB Settings tab switch (KB Settings gated to
            System Manager + Admin, same as desktop). */}
        {canManageKB && (
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="branding">
              <Palette className="mr-2 h-4 w-4" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="brain-settings">
              <Cpu className="mr-2 h-4 w-4" />
              KB Settings
            </TabsTrigger>
          </TabsList>
        )}

        {/* Action Buttons - drive the Branding form only, so they hide
            on the KB Settings tab (which saves inline / via its own bar). */}
        {isAdmin && activeTab === 'branding' && (
          <div className="flex gap-2 justify-center mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={!isDirty}
              className="flex-1 max-w-[120px] border-input"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
            <Button
              size="sm"
              onClick={form.handleSubmit(handleSave)}
              disabled={isSaving || !isDirty}
              className="flex-1 max-w-[120px] bg-primary hover:bg-primary/85 text-primary-foreground"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {/* BRANDING TAB */}
        <TabsContent value="branding" className="mt-0">
        {/* Read-only Notice */}
        {!isAdmin && (
          <Card className="border-primary/30 bg-primary/10 mb-4">
            <CardContent className="py-3">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-foreground">
                  View-only mode. Only Admin users can modify these settings.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6">
            {/* Organization Details */}
            <Card className="bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-foreground">Organization Details</CardTitle>
                <CardDescription className="text-sm">
                  Basic information about your company
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="organization_name"
                  rules={{ required: 'Organization name is required' }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Organization Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter organization name"
                          {...field}
                          disabled={!isAdmin}
                          className="bg-background border-input"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="domain"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Website</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com"
                          {...field}
                          disabled={!isAdmin}
                          className="bg-background border-input"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contact_number"
                  rules={phoneRules}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Contact Number</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="+1 (555) 123-4567"
                          {...field}
                          disabled={!isAdmin}
                          className="bg-background border-input"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Company Logo */}
            <Card className="bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-foreground">Company Logo</CardTitle>
                <CardDescription className="text-sm">
                  Upload your company logo ({TARGET_WIDTH}×{TARGET_HEIGHT}px)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="logo_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <MobileLogoCropUpload
                          value={field.value ? (field.value.startsWith('data:') ? field.value : getImageURL(field.value)) : null}
                          onChange={(logoBase64) => {
                            field.onChange(logoBase64 || '');
                            if (logoBase64) {
                              setLogoFile(logoBase64 as any);
                            } else {
                              setLogoFile(null);
                            }
                          }}
                          disabled={isSaving || !isAdmin}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </form>
        </Form>
        </TabsContent>

        {/* KB SETTINGS TAB - same panel as desktop (Industry / Routing /
            API Keys / Email), gated to System Manager + Admin. */}
        {canManageKB && (
          <TabsContent value="brain-settings" className="mt-0">
            <KBSettingsPanel isSystemManager={isSystemManager} />
          </TabsContent>
        )}
      </div>
    </Tabs>
  );
};

export default MobileCompanyBranding;
